
import React, { useState, useEffect } from 'react';

interface Table {
  id: string;
  name: string;
  capacity: number;
  location: string;
  status: 'available' | 'occupied' | 'reserved' | 'maintenance';
  currentGuest?: {
    name: string;
    startTime: string;
    partySize: number;
    orderId?: string;
  };
}

export default function TableManagement() {
  const [tables, setTables] = useState<Table[]>([
    { id: 'A1', name: 'Meja A1', capacity: 4, location: 'Window View', status: 'available' },
    { id: 'A2', name: 'Meja A2', capacity: 4, location: 'Corner Table', status: 'occupied', 
      currentGuest: { name: 'Keluarga Sato', startTime: '19:30', partySize: 3, orderId: 'ORD-123456' }},
    { id: 'B1', name: 'Meja B1', capacity: 6, location: 'Center Hall', status: 'available' },
    { id: 'B2', name: 'Meja B2', capacity: 6, location: 'Private Booth', status: 'available' },
    { id: 'C1', name: 'Meja C1', capacity: 8, location: 'Family Table', status: 'available' },
    { id: 'VIP1', name: 'VIP Room 1', capacity: 12, location: 'Tatami Style', status: 'reserved' },
    { id: 'VIP2', name: 'VIP Room 2', capacity: 12, location: 'Modern Style', status: 'available' }
  ]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [newTable, setNewTable] = useState({
    name: '',
    capacity: 4,
    location: '',
    status: 'available' as Table['status']
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800';
      case 'occupied': return 'bg-red-100 text-red-800';
      case 'reserved': return 'bg-yellow-100 text-yellow-800';
      case 'maintenance': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'available': return 'ri-check-line';
      case 'occupied': return 'ri-user-line';
      case 'reserved': return 'ri-calendar-line';
      case 'maintenance': return 'ri-tools-line';
      default: return 'ri-table-line';
    }
  };

  const handleTableClick = (table: Table) => {
    if (table.status === 'available') {
      // Set table as occupied
      const guestName = prompt('Nama tamu:');
      const partySize = prompt('Jumlah tamu:');
      
      if (guestName && partySize) {
        setTables(prev => prev.map(t => 
          t.id === table.id 
            ? {
                ...t,
                status: 'occupied' as const,
                currentGuest: {
                  name: guestName,
                  startTime: new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }),
                  partySize: parseInt(partySize)
                }
              }
            : t
        ));
      }
    } else if (table.status === 'occupied') {
      // Clear table
      const confirm = window.confirm(`Kosongkan ${table.name}?`);
      if (confirm) {
        setTables(prev => prev.map(t => 
          t.id === table.id 
            ? { ...t, status: 'available' as const, currentGuest: undefined }
            : t
        ));
      }
    }
  };

  const addTable = () => {
    if (!newTable.name || !newTable.location) {
      alert('Nama meja dan lokasi wajib diisi!');
      return;
    }

    const tableId = 'T' + Date.now().toString().slice(-4);
    const table: Table = {
      id: tableId,
      name: newTable.name,
      capacity: newTable.capacity,
      location: newTable.location,
      status: newTable.status
    };

    setTables(prev => [...prev, table]);
    setNewTable({ name: '', capacity: 4, location: '', status: 'available' });
    setShowAddModal(false);
  };

  const deleteTable = (tableId: string) => {
    const confirm = window.confirm('Yakin ingin menghapus meja ini?');
    if (confirm) {
      setTables(prev => prev.filter(t => t.id !== tableId));
    }
  };

  const updateTableStatus = (tableId: string, status: Table['status']) => {
    setTables(prev => prev.map(t => 
      t.id === tableId 
        ? { ...t, status, currentGuest: status === 'available' ? undefined : t.currentGuest }
        : t
    ));
  };

  const availableTables = tables.filter(t => t.status === 'available').length;
  const occupiedTables = tables.filter(t => t.status === 'occupied').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Manajemen Meja</h2>
          <p className="text-gray-600">Kelola meja restaurant dan status ketersediaan</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line w-4 h-4 flex items-center justify-center"></i>
          <span>Tambah Meja</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
              <i className="ri-check-line text-white w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-700">{availableTables}</div>
              <div className="text-green-600 text-sm">Tersedia</div>
            </div>
          </div>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
              <i className="ri-user-line text-white w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-700">{occupiedTables}</div>
              <div className="text-red-600 text-sm">Terisi</div>
            </div>
          </div>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-yellow-600 rounded-lg flex items-center justify-center">
              <i className="ri-calendar-line text-white w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-700">
                {tables.filter(t => t.status === 'reserved').length}
              </div>
              <div className="text-yellow-600 text-sm">Reserved</div>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <i className="ri-table-line text-white w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-700">{tables.length}</div>
              <div className="text-blue-600 text-sm">Total Meja</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tables Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {tables.map((table) => (
          <div
            key={table.id}
            className={`border-2 rounded-xl p-4 cursor-pointer transition-all duration-300 hover:shadow-lg ${
              table.status === 'available' 
                ? 'border-green-200 bg-green-50 hover:border-green-400' :
              table.status === 'occupied' 
                ? 'border-red-200 bg-red-50 hover:border-red-400' :
              table.status === 'reserved' 
                ? 'border-yellow-200 bg-yellow-50 hover:border-yellow-400' :
                'border-gray-200 bg-gray-50 hover:border-gray-400'
            }`}
            onClick={() => handleTableClick(table)}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{table.name}</h3>
                <p className="text-gray-600 text-sm">{table.location}</p>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusColor(table.status)}`}>
                  {table.status === 'available' ? 'Tersedia' :
                   table.status === 'occupied' ? 'Terisi' :
                   table.status === 'reserved' ? 'Reserved' : 'Maintenance'}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteTable(table.id);
                  }}
                  className="text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                >
                  <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                </button>
              </div>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <i className="ri-group-line w-4 h-4 flex items-center justify-center"></i>
                <span>Kapasitas: {table.capacity} orang</span>
              </div>
              
              {table.currentGuest && (
                <div className="bg-white/70 rounded-lg p-3 space-y-1">
                  <div className="flex items-center space-x-2 text-sm">
                    <i className="ri-user-line w-4 h-4 flex items-center justify-center text-gray-500"></i>
                    <span className="font-semibold text-gray-800">{table.currentGuest.name}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                    <span>Mulai: {table.currentGuest.startTime}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <i className="ri-group-line w-4 h-4 flex items-center justify-center"></i>
                    <span>{table.currentGuest.partySize} tamu</span>
                  </div>
                </div>
              )}
            </div>

            {/* Status Actions */}
            <div className="flex space-x-2">
              {table.status !== 'available' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    updateTableStatus(table.id, 'available');
                  }}
                  className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  Kosongkan
                </button>
              )}
              
              {table.status === 'available' && (
                <select
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => updateTableStatus(table.id, e.target.value as Table['status'])}
                  value={table.status}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 pr-8"
                >
                  <option value="available">Tersedia</option>
                  <option value="occupied">Terisi</option>
                  <option value="reserved">Reserved</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add Table Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-xl font-bold text-gray-900">Tambah Meja Baru</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line w-6 h-6 flex items-center justify-center"></i>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nama Meja *</label>
                <input
                  type="text"
                  value={newTable.name}
                  onChange={(e) => setNewTable({...newTable, name: e.target.value})}
                  placeholder="contoh: Meja D1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kapasitas</label>
                <select
                  value={newTable.capacity}
                  onChange={(e) => setNewTable({...newTable, capacity: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 pr-8"
                >
                  <option value={2}>2 orang</option>
                  <option value={4}>4 orang</option>
                  <option value={6}>6 orang</option>
                  <option value={8}>8 orang</option>
                  <option value={10}>10 orang</option>
                  <option value={12}>12 orang</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Lokasi *</label>
                <input
                  type="text"
                  value={newTable.location}
                  onChange={(e) => setNewTable({...newTable, location: e.target.value})}
                  placeholder="contoh: Near Kitchen"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status Awal</label>
                <select
                  value={newTable.status}
                  onChange={(e) => setNewTable({...newTable, status: e.target.value as Table['status']})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 pr-8"
                >
                  <option value="available">Tersedia</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Batal
              </button>
              <button
                onClick={addTable}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Tambah Meja
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
